import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';

import { cloneDeep } from 'lodash';

import classNames from 'classnames';

import { useIsOverflow } from '@app/hooks/use-overflow';

import { Skeleton } from '@ui';

import {
  useGetChatWordsNGramStaticCloudQuery,
  useGetWordsNGramStaticCloudQuery,
} from '@app/store/api/get-words-n-gramm.api';

import useTranslation from '@app/hooks/use-translation';

import { WordCloudContext } from '../types/types';
import { fillPercentColorStyle } from '../styles/styles';

export const TableContentNGramCloud = () => {
  const { t } = useTranslation('pages.wordsNGramm');
  const { data: dataForCloud, currentTemplate } = useOutletContext<WordCloudContext>();
  const { data: dataVoice, isFetching: isFetchingVoice } = useGetWordsNGramStaticCloudQuery(
    dataForCloud,
    {
      skip: currentTemplate === 'chat' || dataForCloud === undefined,
      refetchOnMountOrArgChange: true,
    },
  );
  const { data: dataChat, isFetching: isFetchingChat } = useGetChatWordsNGramStaticCloudQuery(
    dataForCloud,
    {
      skip: currentTemplate === 'voice' || dataForCloud === undefined,
      refetchOnMountOrArgChange: true,
    },
  );
  const data = useMemo(() => ({ chat: dataChat, voice: dataVoice }), [dataChat, dataVoice]);
  const isFetching = isFetchingChat || isFetchingVoice;

  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const [overflow, setOverflow] = useState<boolean>(false);
  const [value, setValue] = useState<number>();

  useIsOverflow(ref, (value) => setOverflow(value));

  function getDataForTable() {
    if (!data[currentTemplate]?.cloud.length) return;
    const stat = cloneDeep(data[currentTemplate]?.cloud || []);
    const ARRAY_STAT_SPLICER = 9;
    const statisticLength = Math.ceil(stat.length / ARRAY_STAT_SPLICER);
    return new Array(statisticLength).fill(null).map(() => stat?.splice(0, ARRAY_STAT_SPLICER));
  }

  function percentCalc(value: number) {
    if (!data[currentTemplate]?.cloud?.[0]?.freq) return 100;
    return (value * 100) / (data[currentTemplate]?.cloud?.[0]?.freq || 100);
  }
  function onScroll(event) {
    setValue(event.currentTarget.scrollLeft);
  }
  useEffect(() => {
    const el = ref;
    if (el) {
      const onWheel = (e) => {
        if (e.deltaY == 0) return;
        e.preventDefault();
        el.scrollTo({
          left: el.scrollLeft + e.deltaY,
          behavior: 'auto',
        });
      };
      el.addEventListener('wheel', onWheel);
      return () => el.removeEventListener('wheel', onWheel);
    }
  }, [data, ref]);

  if (isFetching) return <Skeleton height={550} />;
  return (
    <>
      <div
        ref={(ref) => setRef(ref)}
        // onWheel={onScroll}
        onScroll={onScroll}
        className={classNames('scrollHidden', 'w-full pb-[14px] overflow-y-hidden transition-all')}
      >
        <div className="flex w-fit gap-[15px]">
          {getDataForTable()?.map((item, index) => (
            <div className="" key={index}>
              <div className="flex font-[600] text-[12px] leading-[110%] tracking-tight !text-[#000] justify-between px-[30px] mb-[18px]">
                <span>{t('word')}</span>
                <span>{t('meets')}</span>
              </div>
              <div className="flex flex-col items-start gap-[4px]">
                {item.map((wordsItems, index) => {
                  return (
                    <div
                      className="relative w-[310px] bg-white h-[45px] rounded-[10px]"
                      key={index}
                    >
                      <div
                        style={{ width: `${percentCalc(wordsItems.freq)}%` }}
                        className={fillPercentColorStyle()}
                      ></div>
                      <div className="z-[1] absolute w-full h-full flex items-center justify-between px-[30px]">
                        <span className="font-[600] text-[14px] leading-[30px] tracking-tight text-0color">
                          {wordsItems.text.charAt(0).toUpperCase() + wordsItems.text.slice(1)}
                        </span>
                        <span className="pr-[20px] font-[400] text-[14px] leading-[30px] tracking-tight text-0color">
                          {wordsItems.freq}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={'absolute bottom-[-10px]'}>
        {overflow && (
          <input
            type="range"
            className="range_thumb w-[650px]"
            max={ref?.scrollWidth && ref?.scrollWidth - 1065}
            value={value}
            min={0}
            defaultValue={0}
          />
        )}
      </div>
    </>
  );
};
